import React, { useEffect, useState } from 'react';
import { Bar, BarChart, Cell, LabelList, ResponsiveContainer, Tooltip, XAxis, YAxis } from 'recharts';
import api from '../../services/api';
import styles from '../../assets/styles/LayoutShared.module.css';
import CartaoExpansivel from './CartaoExpansivel';
import AvisoDadosDesatualizados from './AvisoDadosDesatualizados';
import { formatarKg, formatarNumero, formatarRendimento } from '../../utils/formatters';

interface GrupoEsforco {
  chave: string;
  rotulo: string;
  pescarias: number;
  pctPescarias: number;
  kg: number;
  rendimento: number | null;
  uid_pesca_apetrecho?: string | null;
}

interface DadosEsforco {
  totais: { pescarias: number; kg: number; rendimento: number | null };
  petrechos: GrupoEsforco[];
  petrechosCobertura: { pescarias: number; pescariasComPetrecho: number; pescariasNaoInformado: number; petrechosPorPescaria: number | null };
  tripulacao: GrupoEsforco[];
  tamanhoMedioGrupo?: number | null;
  embarcacao: GrupoEsforco[];
}

interface EsforcoPescaProps {
  /** Query string dos filtros globais (sem '?'), a mesma do /pesca/stats. */
  filtrosQuery: string;
  /** Petrecho hoje filtrado (uid), pra destacar a barra. */
  petrechoSelecionado: string | null;
  /** Clique numa barra de petrecho: liga/desliga o filtro de petrecho do Dashboard. */
  onSelecionarPetrecho: (uid: string | null, nome: string | null) => void;
}

const COR_BARRA = '#00c3ff';
const COR_DESTAQUE = '#f75a68';
const COR_NAO_INFORMADO = '#92989b';

const formatarPct = (pct: number) => (pct > 0 && pct < 1 ? '<1%' : `${formatarNumero(pct, pct < 10 ? 1 : 0)}%`);
const textoRendimento = (r: number | null) => (r == null ? 'n/d' : formatarRendimento(r));
const rotuloBarra = (g: GrupoEsforco) => `${formatarPct(g.pctPescarias)} · ${formatarKg(g.kg)} kg · ${textoRendimento(g.rendimento)}`;

const TooltipEsforco = ({ active, payload, rotuloRendimento }: any) => {
  if (!active || !payload?.length) return null;
  const g: GrupoEsforco = payload[0].payload;
  return (
    <div style={{ background: '#fff', borderRadius: 8, boxShadow: '0 4px 12px rgba(0,0,0,0.12)', padding: '8px 12px', fontSize: '0.8rem', color: '#444' }}>
      <strong>{g.rotulo}</strong><br />
      Pescarias: {formatarNumero(g.pescarias, 0)} ({formatarPct(g.pctPescarias)})<br />
      Produção: {formatarKg(g.kg)} kg<br />
      {rotuloRendimento}: {g.rendimento == null ? 'n/d' : `${formatarRendimento(g.rendimento)} kg/pescador-dia`}
    </div>
  );
};

interface BarrasProps {
  titulo: string;
  grupos: GrupoEsforco[];
  carregando: boolean;
  destaque?: string | null;
  onClicar?: (g: GrupoEsforco) => void;
  /** Texto do rendimento no tooltip: diz de QUAIS pescarias é o número. */
  rotuloRendimento: string;
}

const BarrasEsforco: React.FC<BarrasProps> = ({ titulo, grupos, carregando, destaque, onClicar, rotuloRendimento }) => {
  const dados = grupos.map((g) => ({ ...g, value: g.pctPescarias, rotuloBarra: rotuloBarra(g) }));
  const altura = Math.max(210, dados.length * 30 + 50);
  return (
    <CartaoExpansivel titulo={titulo} alturaNormal={altura}>
      {(h) => {
        const expandido = typeof h === 'string';
        if (!carregando && dados.length === 0) return <p style={{ padding: '20px', color: '#999' }}>Nenhuma pescaria para os filtros atuais.</p>;
        return (
          <div style={{ width: '100%', height: expandido ? `max(${h}, ${dados.length * 30 + 50}px)` : h, padding: '4px 20px 12px 10px', boxSizing: 'border-box', opacity: carregando ? 0.6 : 1 }}>
            <ResponsiveContainer width="100%" height="100%">
              <BarChart
                layout="vertical"
                data={dados}
                margin={{ left: 0, right: 210 }}
                onClick={(d: any) => {
                  const alvo = d && dados.find((x) => x.rotulo === d.activeLabel);
                  if (alvo && onClicar) onClicar(alvo);
                }}
              >
                <XAxis type="number" hide domain={[0, 'dataMax']} />
                <YAxis dataKey="rotulo" type="category" width={150} axisLine={false} tickLine={false} style={{ fontSize: '12px', fontWeight: 'bold', cursor: onClicar ? 'pointer' : 'default' }} />
                <Tooltip cursor={{ fill: '#f1f4f6' }} content={<TooltipEsforco rotuloRendimento={rotuloRendimento} />} />
                <Bar dataKey="value" radius={[0, 4, 4, 0]} barSize={18} style={{ cursor: onClicar ? 'pointer' : 'default' }}>
                  {dados.map((g) => (
                    <Cell key={g.chave} fill={g.chave === 'ni' ? COR_NAO_INFORMADO : g.uid_pesca_apetrecho && g.uid_pesca_apetrecho === destaque ? COR_DESTAQUE : COR_BARRA} />
                  ))}
                  <LabelList dataKey="rotuloBarra" position="right" style={{ fontSize: '11px', fill: '#555' }} />
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          </div>
        );
      }}
    </CartaoExpansivel>
  );
};

const EsforcoPesca: React.FC<EsforcoPescaProps> = ({ filtrosQuery, petrechoSelecionado, onSelecionarPetrecho }) => {
  const [dados, setDados] = useState<DadosEsforco | null>(null);
  const [carregando, setCarregando] = useState(true);
  const [erro, setErro] = useState<string | null>(null);
  const [tentativa, setTentativa] = useState(0); // "Tentar de novo" refaz a busca sem mudar os filtros

  useEffect(() => {
    let ativo = true;
    setCarregando(true);
    api.get(`/pesca/stats/esforco?${filtrosQuery}`)
      .then((res) => { if (ativo) { setDados(res.data.data); setErro(null); } })
      // mantém os dados anteriores na tela e avisa que estão desatualizados
      .catch((e) => { if (ativo) setErro(e?.response?.data?.message || 'Falha ao carregar o esforço de pesca.'); })
      .finally(() => { if (ativo) setCarregando(false); });
    return () => { ativo = false; };
  }, [filtrosQuery, tentativa]);

  const cob = dados?.petrechosCobertura;
  const legendaPetrechos = 'Rótulos: % das pescarias que usaram o petrecho · kg convertido dessas pescarias · rendimento das pescarias com este petrecho (kg/pescador-dia).';
  const legendaGrupo = 'Rótulos: % das pescarias do grupo · kg convertido · rendimento das pescarias deste grupo (kg/pescador-dia).';

  return (
    <div style={{ marginTop: '10px' }}>
      <AvisoDadosDesatualizados erro={!!erro} comDadosAnteriores={!!dados} detalhe={erro} carregando={carregando} onTentarDeNovo={() => setTentativa((t) => t + 1)} />

      <div className={styles.sectionCard}>
        <div className={styles.sectionHeader}>
          Esforço de pesca · Petrechos {petrechoSelecionado && <small>(clique na barra de novo para limpar o filtro)</small>}
        </div>
        <p style={{ margin: '0 20px 6px', fontSize: '0.78rem', color: '#777', lineHeight: 1.45 }}>
          {legendaPetrechos}{' '}
          <strong>Uma pescaria que usou mais de um petrecho entra em cada um deles</strong>
          {cob && cob.petrechosPorPescaria != null && <> (média de {formatarNumero(cob.petrechosPorPescaria, 2)} petrechos por pescaria)</>}
          : por isso as barras somam mais de 100% e os kg e pescarias por petrecho não somam o Total.
        </p>
        <BarrasEsforco
          titulo="Esforço de pesca · Petrechos"
          grupos={(dados?.petrechos ?? []).map((p) => ({ ...p, chave: p.uid_pesca_apetrecho ?? 'ni' }))}
          carregando={carregando}
          rotuloRendimento="Rendimento das pescarias com este petrecho"
          destaque={petrechoSelecionado}
          onClicar={(g) => {
            if (!g.uid_pesca_apetrecho) return; // "Não informado" não é filtro
            onSelecionarPetrecho(g.uid_pesca_apetrecho === petrechoSelecionado ? null : g.uid_pesca_apetrecho, g.rotulo);
          }}
        />
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '30px', marginTop: '10px' }}>
        <div className={styles.sectionCard}>
          <div className={styles.sectionHeader}>
            Tripulação (pescadores por pescaria)
            {dados?.tamanhoMedioGrupo != null && <small> · média de {formatarNumero(dados.tamanhoMedioGrupo, 1)} pescadores por pescaria</small>}
          </div>
          <p style={{ margin: '0 20px 6px', fontSize: '0.78rem', color: '#777' }}>{legendaGrupo} Média do tamanho do grupo considera só as pescarias com o número informado.</p>
          <BarrasEsforco titulo="Tripulação (pescadores por pescaria)" grupos={dados?.tripulacao ?? []} carregando={carregando} rotuloRendimento="Rendimento das pescarias deste grupo" />
        </div>
        <div className={styles.sectionCard}>
          <div className={styles.sectionHeader}>Embarcação</div>
          <p style={{ margin: '0 20px 6px', fontSize: '0.78rem', color: '#777' }}>{legendaGrupo}</p>
          <BarrasEsforco titulo="Embarcação" grupos={dados?.embarcacao ?? []} carregando={carregando} rotuloRendimento="Rendimento das pescarias deste grupo" />
        </div>
      </div>
    </div>
  );
};

export default EsforcoPesca;
